// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	ArrowPathIcon,
	BanknotesIcon,
	EyeIcon,
	EyeSlashIcon,
	ExclamationCircleIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { faBuildingColumns, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useFetchIDPartner from "../useFetchIDPartner";
import WithdrawalForm from "../WithdrawalForm";
import { URL } from "../../service/Service";
import { RecordStatusBadge } from "../../components/status/RecordStatusBadge";
import { isPendingRecordStatus } from "../../utils/recordStatus";
import { useTranslation } from "../../i18n/LanguageContext";
import FeedbackToast from "../../components/FeedbackToast";

export const Balance = () => {
	const { t } = useTranslation();
	const jwtToken = sessionStorage.getItem("jwt");
	const { loading, partnerID } = useFetchIDPartner(jwtToken);

	const [records, setRecords] = useState([]);
	const [balanceData, setBalanceData] = useState({ balance: 0 });
	const [isBalanceVisible, setIsBalanceVisible] = useState(false);
	const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
	const [transactionType, setTransactionType] = useState("ALL");
	const [errorMessage, setErrorMessage] = useState("");
	const [updatingBalance, setUpdatingBalance] = useState(false);
	const [updateFeedback, setUpdateFeedback] = useState(null);

	const fetchBalanceData = useCallback(async () => {
		if (!partnerID || !jwtToken) return;

		try {
			const response = await fetch(`${URL}/api/partners/${partnerID}/balance`, {
				headers: {
					Authorization: `Bearer ${jwtToken}`,
				},
			});

			if (!response.ok) {
				throw new Error("Não foi possível carregar o saldo.");
			}

			const data = await response.json();
			setBalanceData(data || { balance: 0 });
		} catch (error) {
			console.error("Erro ao carregar saldo:", error);
		}
	}, [partnerID, jwtToken]);

	const fetchRecords = useCallback(async () => {
		if (!partnerID || !jwtToken) return;

		try {
			const response = await fetch(`${URL}/api/partners/${partnerID}/records`, {
				headers: {
					Authorization: `Bearer ${jwtToken}`,
					"Content-Type": "application/json",
				},
			});

			if (response.status === 204) {
				setRecords([]);
				return;
			}

			if (!response.ok) {
				const responseText = await response.text();
				throw new Error(responseText || "Não foi possível carregar registos.");
			}

			const data = await response.json();

			const filteredData = Array.isArray(data)
				? data.filter(
						(item) => Number(item?.partnerRecordDTO?.recordAmount || 0) !== 0
				  )
				: [];

			setRecords(filteredData);
		} catch (error) {
			console.error("Erro ao carregar registos:", error);
			setRecords([]);
		}
	}, [partnerID, jwtToken]);

	useEffect(() => {
		if (!loading && partnerID) {
			fetchBalanceData();
			fetchRecords();
		}
	}, [loading, partnerID, fetchBalanceData, fetchRecords]);

	const hasPendingWithdrawal = () => {
		return records.some((item) => {
			const record = item?.partnerRecordDTO;

			if (!record) {
				return false;
			}

			return (
				record.recordType === "WITHDRAWAL" &&
				isPendingRecordStatus(record.recordStatus, record.recordType)
			);
		});
	};

	const handleOpenWithdrawalModal = () => {
		if (hasPendingWithdrawal()) {
			setErrorMessage(t('balance.pendingError'));

			window.setTimeout(() => {
				setErrorMessage("");
			}, 5000);

			return;
		}

		setErrorMessage("");
		setShowWithdrawalModal(true);
	};

	const handleCloseWithdrawalModal = () => {
		setShowWithdrawalModal(false);
	};

	const refreshPageData = async () => {
		await fetchBalanceData();
		await fetchRecords();
	};

	const readErrorMessage = async (response) => {
		const responseText = await response.text();
		if (!responseText) return t('balance.updateError');

		try {
			return JSON.parse(responseText)?.message || t('balance.updateError');
		} catch {
			return responseText;
		}
	};

	const waitForBalanceUpdate = async () => {
		for (let attempt = 0; attempt < 40; attempt += 1) {
			await new Promise((resolve) => window.setTimeout(resolve, 1000));

			const response = await fetch(`${URL}/api/syncPartnerTransactions/${partnerID}`, {
				headers: { Authorization: `Bearer ${jwtToken}` },
			});

			if (!response.ok) {
				throw new Error(await readErrorMessage(response));
			}

			const job = await response.json();
			if (job?.status === "COMPLETED") return job;
			if (job?.status === "ERROR") {
				throw new Error(job?.errorMessage || t('balance.updateError'));
			}
		}

		throw new Error(t('balance.updateError'));
	};

	const handleUpdateBalance = async () => {
		if (!partnerID || !jwtToken || updatingBalance) return;

		try {
			setUpdatingBalance(true);
			setErrorMessage("");
			setUpdateFeedback(null);

			const response = await fetch(`${URL}/api/${partnerID}/triggerTotalCalculation`, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${jwtToken}`,
					"Content-Type": "application/json",
				},
			});

			if (response.status === 403) {
				throw new Error(t('balance.cooldownBlocked'));
			}

			if (!response.ok && response.status !== 409) {
				throw new Error(await readErrorMessage(response));
			}

			const job = await waitForBalanceUpdate();
			await refreshPageData();
			const hasTransactions = Array.isArray(job?.data) && job.data.length > 0;
			setUpdateFeedback({
				type: hasTransactions ? 'success' : 'info',
				title: t('balance.syncCompletedTitle'),
				message: hasTransactions ? t('balance.updateSuccess') : t('balance.noNewTransactions'),
			});
		} catch (error) {
			console.error("Erro ao atualizar saldo:", error);
			setUpdateFeedback({
				type: 'error',
				title: t('balance.syncFailedTitle'),
				message: error?.message || t('balance.updateError'),
			});
		} finally {
			setUpdatingBalance(false);
		}
	};

	const formatMoney = (value) => {
		const numberValue = Number(value || 0);
		return `${numberValue.toFixed(2).replace(".", ",")}\u00A0€`;
	};

	const filteredRecords = useMemo(() => {
		if (transactionType === "ALL") return records;

		return records.filter(
			(item) => item?.partnerRecordDTO?.recordType === transactionType
		);
	}, [records, transactionType]);

	const handleCancelPending = async (recordID) => {
		if (!recordID || !partnerID || !jwtToken) return;

		try {
			const response = await fetch(
				`${URL}/api/partners/${partnerID}/records/${recordID}`,
				{
					method: "PATCH",
					headers: {
						Authorization: `Bearer ${jwtToken}`,
						"Content-Type": "application/json",
					},
				}
			);

			const responseText = await response.text();

			if (!response.ok) {
				let message = t('balance.cancelError');

				try {
					const parsed = JSON.parse(responseText);
					message = parsed?.message || message;
				} catch {
					message = responseText || message;
				}

				throw new Error(message);
			}

			await refreshPageData();
		} catch (error) {
			console.error("Erro ao cancelar pedido:", error);
			setErrorMessage(
				error?.message || t('balance.cancelError')
			);

			window.setTimeout(() => {
				setErrorMessage("");
			}, 5000);
		}
	};

	return (
		<div className="mx-4 pt-6 md:mx-10 md:pt-7">
			{updateFeedback && (
				<FeedbackToast
					type={updateFeedback.type}
					title={updateFeedback.title}
					message={updateFeedback.message}
					onClose={() => setUpdateFeedback(null)}
				/>
			)}
			<div className="mb-7 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
				<div>
					<p className="text-xs font-bold uppercase tracking-[0.18em] text-green-cl/70">
						{t('balance.management')}
					</p>
					<h1 className="mt-1 text-2xl font-black text-gray-900">
						{t('transactions.title')}
					</h1>
					<p className="mt-1 text-sm text-gray-500">{t('transactions.description')}</p>
				</div>

				<div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
					<button
						type="button"
						onClick={() => setIsBalanceVisible((visible) => !visible)}
						aria-label={isBalanceVisible ? t('balance.hide') : t('balance.show')}
						className="flex h-[42px] min-w-[220px] items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 text-left shadow-sm transition hover:border-green-cl/30 hover:bg-green-50/40 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2"
					>
						<div>
							<p className="text-[11px] font-medium leading-tight text-gray-500">{t('balance.available')}</p>
							<p className="text-base font-bold leading-tight text-gray-900">
								{isBalanceVisible ? formatMoney(balanceData?.balance || 0) : t('balance.show')}
							</p>
						</div>
						{isBalanceVisible ? <EyeSlashIcon className="h-4 w-4 text-gray-500" /> : <EyeIcon className="h-4 w-4 text-gray-500" />}
					</button>

					<button
						type="button"
						onClick={handleUpdateBalance}
						disabled={updatingBalance}
						className="inline-flex h-[42px] self-center items-center justify-center gap-2 rounded-lg border border-green-cl bg-white px-4 text-sm font-semibold text-green-cl shadow-sm transition hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
					>
						<ArrowPathIcon className={`h-4 w-4 ${updatingBalance ? "animate-spin" : ""}`} />
						{t('balance.update')}
					</button>

					<button
						type="button"
						onClick={handleOpenWithdrawalModal}
						className="inline-flex h-[42px] self-center items-center justify-center gap-2 rounded-lg bg-green-cl px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-green-900 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2"
					>
						<BanknotesIcon className="h-4 w-4" />
						{t('balance.withdraw')}
					</button>
				</div>
			</div>

			{errorMessage && (
				<div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
					{errorMessage}
				</div>
			)}

			<div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
				<div className="flex">
					<ExclamationCircleIcon
						className="h-5 w-5 flex-shrink-0 text-yellow-500"
						aria-hidden="true"
					/>

					<p className="ml-3 text-sm text-yellow-800">
						{t('transactions.noticeBefore')} <strong>{t('transactions.pendingState')}</strong>,{" "}
						<FontAwesomeIcon icon={faXmark} className="mx-1 text-red-700" />{" "}
						{t('transactions.noticeAfter')}
					</p>
				</div>
			</div>

			<div className="mt-7 flex flex-wrap gap-2" aria-label={t('transactions.filterType')}>
				{[
					["ALL", t('transactions.all')],
					["DEPOSIT", t('transactions.deposits')],
					["WITHDRAWAL", t('transactions.withdrawals')],
				].map(([value, label]) => (
					<button
						key={value}
						type="button"
						onClick={() => setTransactionType(value)}
						className={`rounded-xl px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 ${
							transactionType === value
								? "bg-green-cl text-white shadow-sm"
								: "border border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
						}`}
					>
						{label}
					</button>
				))}
			</div>

			<div className="mt-8 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
				<div className="overflow-hidden">
					<table className="w-full table-fixed divide-y divide-gray-200">
						<colgroup>
							<col className="w-[27%]" />
							<col className="w-[15%]" />
							<col className="w-[15%]" />
							<col className="w-[12%]" />
							<col className="w-[18%]" />
							<col className="w-[13%]" />
						</colgroup>

						<thead className="bg-white">
							<tr>
								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									{t('transactions.orderId')}
								</th>

								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									{t('transactions.payment')}
								</th>

								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									{t('transactions.transactions')}
								</th>

								<th className="px-4 py-4 text-center text-xs font-black uppercase text-gray-900">
									{t('transactions.type')}
								</th>

								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									{t('transactions.values')}
								</th>

								<th className="px-4 py-4 text-left text-xs font-black uppercase text-gray-900">
									{t('transactions.status')}
								</th>
							</tr>
						</thead>

						<tbody className="divide-y divide-gray-200 bg-white">
							{filteredRecords.length === 0 && (
								<tr>
									<td
										colSpan={6}
										className="px-6 py-8 text-center text-sm text-gray-500"
									>
										{t('transactions.none')}
									</td>
								</tr>
							)}

							{filteredRecords.map((item) => {
								const record = item.partnerRecordDTO;
								const meta = item.transactionMetaDataDTO;

								const isWithdrawal = record.recordType === "WITHDRAWAL";
								const isPending = isPendingRecordStatus(
									record.recordStatus,
									record.recordType
								);

								return (
									<tr key={record.recordID} className="hover:bg-gray-50">
										<td className="px-4 py-4 text-sm">
											<div className="flex min-w-0 items-center gap-3">
												<FontAwesomeIcon
													icon={faBuildingColumns}
													className="h-5 w-5 flex-shrink-0 text-gray-600"
												/>

												<div className="min-w-0">
													<p
														className="truncate font-medium text-gray-900"
														title={record.recordID}
													>
														{record.recordID}
													</p>

													<p className="mt-0.5 truncate text-xs text-gray-500">
														{record.recordDate}
													</p>
												</div>
											</div>
										</td>

										<td className="px-4 py-4 text-sm text-gray-600">
											<p className="truncate font-semibold text-gray-700">
												{meta?.paymentMethod || "-"}
											</p>

											<p className="mt-0.5 truncate text-xs text-gray-400">
												{t('transactions.client')}: {meta?.customerID || "-"}
											</p>
										</td>

										<td className="px-4 py-4 text-sm text-gray-600">
											<p className="whitespace-nowrap">
												<span className="font-bold text-gray-800">
													{meta?.transactionQuantity || 0}
												</span>{" "}
												{t('transactions.short')}
											</p>

											<p className="mt-0.5 whitespace-nowrap text-xs text-gray-500">
												{meta?.transactionVolume !== undefined &&
												meta?.transactionVolume !== null
													? formatMoney(meta.transactionVolume)
													: "-"}
											</p>
										</td>

										<td className="px-4 py-4 text-center text-sm">
											<div className="flex justify-center">
												<span className="inline-flex items-center justify-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-200">
											{isWithdrawal ? t('transactions.withdrawal') : t('transactions.deposit')}
												</span>
											</div>
										</td>

										<td className="px-4 py-4 text-sm">
											<p className="whitespace-nowrap font-black text-gray-900">
												{isWithdrawal ? "-" : "+"}
												{formatMoney(record.recordAmount)}
											</p>

											<p className="mt-0.5 whitespace-nowrap text-xs font-semibold text-gray-500">
										{t('transactions.balance')}: {formatMoney(record.balance)}
											</p>
										</td>

										<td className="px-4 py-4 text-left text-sm">
											<div className="flex items-center justify-start gap-2">
												<RecordStatusBadge
													status={record.recordStatus}
													recordType={record.recordType}
												/>

												{isWithdrawal && isPending && (
													<button
														type="button"
														onClick={() =>
															handleCancelPending(record.recordID)
														}
												title={t('transactions.cancel')}
														className="inline-flex h-8 w-8 items-center justify-center rounded-xl text-red-600 transition hover:bg-red-50 hover:text-red-800"
													>
														<FontAwesomeIcon icon={faXmark} />
													</button>
												)}
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>

					<div className="border-t border-gray-100 px-6 py-4 text-sm text-gray-500">
						{t('pagination.showing')} {filteredRecords.length > 0 ? 1 : 0} {t('pagination.to')} {filteredRecords.length} {t('pagination.of')}{" "}
						{filteredRecords.length} {t('pagination.results')}
					</div>
				</div>
			</div>

			{showWithdrawalModal && (
				<div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
					<button
						type="button"
						aria-label={t('common.close')}
						onClick={handleCloseWithdrawalModal}
						className="absolute inset-0 cursor-default bg-slate-900/55 backdrop-blur-[1px]"
					/>

					<div className="relative z-10 w-full max-w-[520px] rounded-2xl bg-white shadow-2xl">
						<button
							type="button"
							onClick={handleCloseWithdrawalModal}
							aria-label={t('common.close')}
							className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-cl"
						>
							<XMarkIcon className="h-5 w-5" />
						</button>

						<div className="p-6 pt-8">
							<WithdrawalForm
								onSuccess={async () => {
									await refreshPageData();
									handleCloseWithdrawalModal();
								}}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
